'use client';

import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import CircularProgress from '@enlighten/design-system/dist/CircularProgress';
import Stack from '@enlighten/design-system/dist/Stack';
import Typography from '@enlighten/design-system/dist/Typography';
import dayjs from 'dayjs';
import React from 'react';

import InsightCard from '@/components/pages/common/InsightCard';
import { WP_CONTENTS } from '@/components/wordpress/utils/constants';
import type { WpPost } from '@/types/wp';

/** 로딩·빈 목록·글 한 건이 서로 비슷한 높이가 되게 세 상태가 공유한다. */
const CONTENT_MIN_HEIGHT = 440;

interface Pagination {
	page: number;
	perPage: number;
}

export default function Page() {
	const [data, setData] = React.useState<WpPost[]>([]);
	const [pagination, setPagination] = React.useState<Pagination>({ page: 1, perPage: 9 });

	const [isLoading, setLoading] = React.useState(true);
	const [hasMore, setHasMore] = React.useState(false);

	const renderContent = () => {
		if (data.length === 0 && isLoading) {
			return (
				<Stack justifyContent="center" alignItems="center" minHeight={CONTENT_MIN_HEIGHT}>
					<CircularProgress />
				</Stack>
			);
		}

		if (data.length === 0) {
			return (
				<Stack justifyContent="center" alignItems="center" minHeight={CONTENT_MIN_HEIGHT}>
					<Typography color="grey.700">No insights have been published yet.</Typography>
				</Stack>
			);
		}

		return (
			<>
				<Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(250px, 1fr))" alignContent="start" gap="16px" minHeight={CONTENT_MIN_HEIGHT}>
					{data.map((item) => (
						<InsightCard
							key={item.id}
							url={`/en/insights/${item.id}`}
							thumbnail={item._embedded[`wp:featuredmedia`]?.[0]?.source_url}
							title={item.title.rendered}
							date={dayjs(item.date).format('MMMM D, YYYY')}
						/>
					))}
				</Box>

				{hasMore ? (
					<Button
						variant="outlined"
						onClick={() => {
							setPagination((state) => ({ ...state, page: state.page + 1 }));
						}}
					>
						More
					</Button>
				) : null}
			</>
		);
	};

	React.useEffect(() => {
		setLoading(true);

		fetch(`${process.env.NEXT_PUBLIC_WP_API_URL}/wp-json/wp/v2/posts?categories=${WP_CONTENTS.insight.categoryId}&lang=en&page=${pagination.page}&per_page=${pagination.perPage}&_embed`)
			.then((response) => {
				const totalPages = Number(response.headers.get('X-WP-TotalPages'));

				return response.json().then((posts) => ({ posts: posts as WpPost[], totalPages }));
			})
			.then(({ posts, totalPages }) => {
				setData((prev) => {
					const merged = pagination.page === 1 ? posts : [...prev, ...posts];

					return [...new Map(merged.map((post) => [post.id, post])).values()];
				});
				setHasMore(totalPages ? pagination.page < totalPages : posts.length === pagination.perPage);
			})
			.finally(() => {
				setLoading(false);
			});
	}, [pagination]);

	return (
		<Stack gap="28px">
			<Typography fontSize="22px" fontWeight={700}>
				Energy Insights from ENlighten
			</Typography>

			{renderContent()}
		</Stack>
	);
}
